/** @jsx jsx */
import React, {
	type ChangeEventHandler,
	type KeyboardEventHandler,
	type FormEventHandler,
	type MouseEventHandler,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token, type CSSToken } from '@atlaskit/tokens';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import {
	COLUMN_ID_MERGED_COLUMN,
	COLUMN_ID_CHECKBOX,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { InlineCreateAdditionalFieldsEntrypoint } from '@atlassian/jira-async-inline-create-additional-fields/entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { UserValue } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import type { DueDate } from '@atlassian/jira-inline-card-create-additional-fields/src/InlineCardCreateAdditionalFields.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type {
	IssueToCreate,
	IssueCreateIssueType,
	IssueCreateProject,
	ContextualFields,
} from '../../common/types.tsx';

import {
	IssueCreateContextualFieldsData,
	useIssueCreateContextualFields,
} from '../../controllers/issue-create-contextual-fields-data/index.tsx';
import type IssueTypeDropdownEntrypoint from './issue-type-dropdown/entrypoint.tsx';
import { IssueTypeDropdown } from './issue-type-dropdown/IssueTypeDropdown.tsx';
import messages from './messages.tsx';
import ReturnIcon from './return-icon/index.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useInlineIssueCreateEntryPoint } from './utils';

const ICON_WIDTH = 24;
const PADDING = 8;
const HIERARCHY_LEVEL_PADDING = 12;
const ICC_ICON_PADDING = 6;

export const useIsReady = () => {
	const isReadyRef = useRef(false);
	useEffect(() => {
		setTimeout(() => {
			isReadyRef.current = true;
		}, 100);
	}, []);

	return isReadyRef;
};

const isComposing = (event: React.KeyboardEvent) => event.nativeEvent.isComposing;

export const IssueTypeDropdownFallback = () => (
	<IssueTypeDropdown project={null} onChange={noop} onProjectChange={noop} selectedType={null} />
);

type Appearance = 'row' | 'footer';

type Column = { id: string; defaultWidth?: number; mergedColumns?: { id: string }[] };

const calledFromSource = 'issueTable';

export type InlineCreateFormProps = {
	initialIssueDraft?: Partial<IssueToCreate>; // Remove when cleaning up `memoize_inline_create_handlers`
	getInitialIssueDraft?: () => Partial<IssueToCreate> | undefined;
	onDraftUpdate?: (issueDraft: Partial<IssueToCreate>) => void;
	onCreateIssue: (issue: IssueToCreate, contextualFields: ContextualFields) => void;
	onCancel?: () => void;
	appearance?: Appearance;
	entryPointSubject: EntryPointReferenceSubject<typeof IssueTypeDropdownEntrypoint>;
	depth: number;
	columns?: Column[];
	rankFieldId: string | null | undefined;
	isGrouping?: boolean;
	additionalFieldsEntryPointSubject?: EntryPointReferenceSubject<
		typeof InlineCreateAdditionalFieldsEntrypoint
	> | null;
	location?: string;
	isTimeline?: boolean;
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	canShowAdditionalFields?: boolean;
	isSearchable?: boolean;
	footerBorderRadius?: CSSToken;
};

const InlineCreateForm = ({
	initialIssueDraft: initialIssueDraftProp,
	getInitialIssueDraft,
	onDraftUpdate,
	onCreateIssue,
	onCancel,
	entryPointSubject,
	appearance = 'row',
	depth = 0,
	columns,
	isGrouping,
	additionalFieldsEntryPointSubject,
	location,
	isTimeline,
	canShowAdditionalFields,
	isSearchable,
	footerBorderRadius,
}: InlineCreateFormProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isManageIssueTypesModalOpen, setIsManageIssueTypesModalOpen] = useState<boolean>(false);
	const initialIssueDraft = fg('memoize_inline_create_handlers')
		? getInitialIssueDraft?.()
		: initialIssueDraftProp;
	const [textContent, setTextContent] = useState(initialIssueDraft?.summary ?? '');
	const [selectedType, setSelectedType] = useState<IssueCreateIssueType | null>(
		initialIssueDraft?.issueType ?? null,
	);
	const [selectedProject, setSelectedProject] = useState<IssueCreateProject | null>(
		initialIssueDraft?.project ?? null,
	);

	// assignee would be undefined initially, when user selects unassigned user from picker then it will be null
	// Default assignee is fetched from the entrypoint
	const initialAssignee = useMemo(
		() =>
			initialIssueDraft?.assignee
				? {
						accountId: initialIssueDraft.assignee.accountId,
						displayName: initialIssueDraft.assignee.name,
						avatarUrls: { '48x48': initialIssueDraft.assignee.picture },
				  }
				: undefined,
		[initialIssueDraft?.assignee],
	);
	const [assignee, setAssignee] = useState<UserValue | undefined>(initialAssignee);
	const [dueDate, setDueDate] = useState<DueDate | null>(initialIssueDraft?.dueDate ?? null);
	const [isUpdatingAdditionalFields, setIsUpdatingAdditionalFields] = useState(false);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
		onDraftUpdate?.({
			summary: textContent,
			project: selectedProject ?? undefined,
			issueType: selectedType ?? undefined,
			...(assignee && fg('jira_render_pic_in_nin')
				? {
						assignee: {
							accountId: assignee.accountId ?? '',
							name: assignee.displayName,
							picture: assignee.avatarUrls['48x48'] ?? '',
						},
				  }
				: {}),
			...(dueDate != null && fg('jira_render_pic_in_nin') ? { dueDate } : {}),
		});
	}, [onDraftUpdate, selectedProject, selectedType, textContent, assignee, dueDate]);

	const formRef = useRef<HTMLFormElement | null>(null);
	const isEmptyRef = useRef(true);
	const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
	isEmptyRef.current = textContent.trim().length === 0;
	const contextualFields = useIssueCreateContextualFields();

	// prevent blurring from occurring when the form is first rendered
	const isReadyRef = useIsReady();

	const handleCreateIssue = useCallback(() => {
		if (!isEmptyRef.current && selectedType && selectedProject) {
			onCreateIssue(
				{
					summary: textContent,
					project: selectedProject,
					issueType: selectedType,
					...(assignee && fg('jira_render_pic_in_nin')
						? {
								assignee: {
									accountId: assignee.accountId ?? '',
									name: assignee.displayName ?? '',
									picture: assignee.avatarUrls['48x48'] ?? '',
								},
						  }
						: {}),
					...(dueDate != null && fg('jira_render_pic_in_nin') ? { dueDate } : {}),
				},
				contextualFields,
			);
			setTextContent('');
			setAssignee(undefined);
			setDueDate(null);
		}
	}, [
		contextualFields,
		onCreateIssue,
		selectedProject,
		selectedType,
		textContent,
		assignee,
		dueDate,
	]);

	const handleCreateIssueRef = useRef(handleCreateIssue);
	handleCreateIssueRef.current = handleCreateIssue;

	const handleBlur = useCallback(
		(event: MouseEvent) => {
			if (
				!isReadyRef.current ||
				(isManageIssueTypesModalOpen && fg('work_type_inline_config_nin_icc_fg'))
			) {
				return;
			}

			// make sure to make a local copy of the isEmptyRef value before it gets reset by the create handler
			const formEmpty = isEmptyRef.current;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const inForm = formRef.current?.contains(event.target as Node) ?? false;

			// Don't close ICC when additional fields (assignee/due date pickers) are active
			// This prevents the ICC from closing when users click outside the pickers in NIN for both Software and Business
			if (isUpdatingAdditionalFields && fg('jira_render_pic_in_nin')) {
				if (!inForm) {
					setTimeout(() => {
						textAreaRef.current?.focus();
					}, 0);

					if (fg('jifjam2_icc_fix')) {
						setIsUpdatingAdditionalFields(false);
					}
				}
				return;
			}

			if (!inForm) {
				onCancel?.();
				event.stopPropagation();
				fireUIAnalytics(createAnalyticsEvent({}), 'form blurred', 'inlineCreate', {
					formEmpty,
					escapeKeyPressed: false,
				});
			}
		},
		[
			isReadyRef,
			isManageIssueTypesModalOpen,
			isUpdatingAdditionalFields,
			onCancel,
			createAnalyticsEvent,
		],
	);

	const handleChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((e) => {
		const content = e.target.value.replace(/[\n\r]+/g, ' ');
		setTextContent(content);
	}, []);

	const handleIssueTypeChange = useCallback(
		(newType: IssueCreateIssueType | null, isSelected?: boolean) => {
			setSelectedType(newType);
			if (isSelected) {
				setTimeout(() => {
					textAreaRef.current?.focus();
				}, 0);
			}
		},
		[],
	);

	const handleKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
		(e) => {
			if (e.key === 'Enter' && !isComposing(e)) {
				e.preventDefault();
				handleCreateIssue();
			}
		},
		[handleCreateIssue],
	);

	const handleFormKeyDown = useCallback<KeyboardEventHandler<HTMLFormElement>>(
		(e) => {
			if (!isUpdatingAdditionalFields || !fg('jira_render_pic_in_nin')) {
				e.stopPropagation();
			}
		},
		[isUpdatingAdditionalFields],
	);

	const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault();
			handleCreateIssue();
		},
		[handleCreateIssue],
	);

	const handleCreateButtonClick = useCallback<MouseEventHandler<HTMLElement>>(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'inlineCreateSubmit');
	}, [createAnalyticsEvent]);

	useEffect(() => {
		// on mount, we want to move the cursor to the end of the input
		// TODO; Add test for this feature once we support pre-population of the summary
		textAreaRef.current?.setSelectionRange(
			textAreaRef.current.value.length,
			textAreaRef.current.value.length,
		);
		fireUIAnalytics(createAnalyticsEvent({}), 'form focused', 'inlineCreate', {
			location,
		});
	}, [createAnalyticsEvent, location]);

	const handleEscape = useCallback(
		(e: KeyboardEvent) => {
			const isAdditionalFieldsPickersActive =
				isUpdatingAdditionalFields && fg('jira_render_pic_in_nin');

			if (
				!isAdditionalFieldsPickersActive &&
				!(isManageIssueTypesModalOpen && fg('work_type_inline_config_nin_icc_fg'))
			) {
				e.stopPropagation();

				if (e.key === 'Escape') {
					onCancel?.();
					fireUIAnalytics(createAnalyticsEvent({}), 'form blurred', 'inlineCreate', {
						formEmpty: isEmptyRef.current,
						escapeKeyPressed: true,
					});
				}
			}
			if (e.key === 'Enter') {
				if (fg('jira_render_pic_in_nin')) {
					if (e.target === textAreaRef.current) {
						e.preventDefault();
						handleCreateIssue();
					}
				} else {
					e.preventDefault();
					handleCreateIssue();
				}
			}
		},
		[
			isUpdatingAdditionalFields,
			isManageIssueTypesModalOpen,
			onCancel,
			createAnalyticsEvent,
			handleCreateIssue,
		],
	);

	useEffect(() => {
		if (!__SERVER__) {
			document.addEventListener('click', handleBlur, true);
			document.addEventListener('keydown', handleEscape, true);

			// eslint-disable-next-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop
			document.addEventListener('dragstart', handleBlur, true);

			return () => {
				document.removeEventListener('click', handleBlur, true);
				document.removeEventListener('keydown', handleEscape, true);
				document.removeEventListener('dragstart', handleBlur, true);
			};
		}
	}, [handleBlur, handleEscape]);

	const padding = fg('jira_list_merged_column_gate') ? HIERARCHY_LEVEL_PADDING : PADDING;
	let paddingLeft = depth > 1 ? (depth - 1) * ICON_WIDTH + padding : padding;

	const mergedColumn = fg('jira_list_merged_column_gate')
		? columns?.find(({ id }) => id === COLUMN_ID_MERGED_COLUMN)
		: undefined;

	if (mergedColumn && !isGrouping) {
		// For merged columns, use the same padding calculation as MergedCell component:
		// paddingLeft: `${HIERARCHY_LEVEL_PADDING * depth + CELL_PADDING}px`
		// This matches the logic in MergedCell.tsx minus the ICC icon padding;
		paddingLeft = HIERARCHY_LEVEL_PADDING * depth + PADDING - ICC_ICON_PADDING;

		// Add checkbox column width if present
		const checkboxColumnWidth =
			columns?.find(({ id }) => id === COLUMN_ID_CHECKBOX)?.defaultWidth ?? 0;
		paddingLeft += checkboxColumnWidth;

		// Add expand icon width if there are merged fields (hierarchy chevron space)
		const hasMergedFields = Number(mergedColumn.mergedColumns?.length) > 0;
		if (hasMergedFields) {
			paddingLeft += ICON_WIDTH;
		}
	}

	const shouldShowAdditionalFields = useCallback(
		() => expVal('jira_powerful_inline_create_for_nin', 'isEnabled', false),
		[],
	);

	return (
		// TODO: Check if we can catch the escape in a different level that removes below lint rule
		<FullContent
			ref={formRef}
			onKeyDown={handleFormKeyDown}
			onSubmit={handleSubmit}
			appearance={appearance}
			paddingLeft={paddingLeft}
			{...(fg('refactor_issue_table_for_timline')
				? {
						footerBorderRadius,
				  }
				: {
						isTimeline,
				  })}
			isMakeSpaceForRovoEnabled={fg('global_rovo_button_in_jira')}
		>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointSubject}
				id="async-issue-create-issue-type-dropdown"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				fallback={<IssueTypeDropdownFallback />}
				runtimeProps={{
					onChange: fg('yurree_jjj2_timeline') ? handleIssueTypeChange : setSelectedType,
					onProjectChange: setSelectedProject,
					setIsEditing: fg('jifjam2_icc_fix') ? setIsUpdatingAdditionalFields : undefined,
					setIsManageIssueTypesModalOpen: fg('work_type_inline_config_nin_icc_fg')
						? setIsManageIssueTypesModalOpen
						: undefined,
					isManageIssueTypesModalOpen: fg('work_type_inline_config_nin_icc_fg')
						? isManageIssueTypesModalOpen
						: undefined,
					selectedType,
					isSearchable,
				}}
			/>
			<Box
				testId="issue-table-inline-issue-create.ui.inline-create-form.textarea"
				xcss={TextAreaStyles}
				as="textarea"
				autoFocus
				aria-label={formatMessage(messages.placeholderText)}
				placeholder={formatMessage(messages.placeholderText)}
				value={textContent}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				ref={textAreaRef}
			/>
			{canShowAdditionalFields &&
				additionalFieldsEntryPointSubject != null &&
				selectedProject?.key &&
				fg('jira_render_pic_in_nin') && (
					<JiraEntryPointContainer
						entryPointReferenceSubject={additionalFieldsEntryPointSubject}
						id="async-inline-create-additional-fields"
						packageName="async-inline-create-additional-fields"
						teamName="jira-werewolves"
						fallback={null}
						errorFallback="unmount"
						runtimeProps={{
							projectKey: selectedProject?.key,
							fullWidth: false,
							isCompactDueDatePicker: true,
							shouldRenderToParent: true,
							shouldShowNameLabel: false,
							strategy: 'fixed',
							onAssigneeUpdate: setAssignee,
							defaultAssignee: assignee,
							defaultDueDate: dueDate,
							onDateSelected: setDueDate,
							shouldShowAdditionalFields,
							source: calledFromSource,
							onUpdatingAssigneePicker: setIsUpdatingAdditionalFields,
							onUpdatingDueDatePicker: setIsUpdatingAdditionalFields,
							issueTypeId: selectedType?.issueTypeId,
						}}
					/>
				)}
			<Flex gap="space.100" alignItems="center" justifyContent="end">
				<StyledCreateButton
					appearance="primary"
					iconAfter={<ReturnIcon />}
					isDisabled={isEmptyRef.current}
					spacing="compact"
					type="submit"
					onClick={handleCreateButtonClick}
				>
					{formatMessage(messages.createButton)}
				</StyledCreateButton>
			</Flex>
		</FullContent>
	);
};

export type Props = InlineCreateFormProps & {
	contextualFields?: ContextualFields;
};

const InlineCreateFormWithContainer = ({ contextualFields, ...props }: Props) => (
	<JSErrorBoundary fallback="flag" id="issue-table-inline-issue-create">
		<IssueCreateContextualFieldsData payload={contextualFields}>
			<InlineCreateForm {...props} />
		</IssueCreateContextualFieldsData>
	</JSErrorBoundary>
);

export default memo(InlineCreateFormWithContainer);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullContentNew = styled.form<
	Pick<InlineCreateFormProps, 'appearance' | 'footerBorderRadius'> & {
		paddingLeft: number;
		isMakeSpaceForRovoEnabled: boolean;
	}
>(
	{
		display: 'flex',
		gap: token('space.100'),

		backgroundColor: token('elevation.surface'),
		transition: 'box-shadow 200ms ease-in-out',
		boxShadow: `inset 0 0 0 ${token('border.width.focused')} ${token('color.border.focused')}`,
		boxSizing: 'border-box',
		outline: 'none',
		minHeight: '40px',
		flexDirection: 'row',
		alignItems: 'center',
		height: '40px',
		margin: '0px', // margin: '0px' is needed to override the default margin of the form element
		paddingTop: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		paddingRight: ({ isMakeSpaceForRovoEnabled }) =>
			isMakeSpaceForRovoEnabled ? token('space.300') : token('space.100'),
		paddingBottom: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		paddingLeft: (props) => `${props.paddingLeft}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ footerBorderRadius }: { footerBorderRadius?: CSSToken }) =>
		footerBorderRadius && { borderRadius: footerBorderRadius },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ footerBorderRadius, appearance }: { footerBorderRadius?: CSSToken; appearance?: string }) =>
		!footerBorderRadius &&
		appearance === 'footer' && {
			borderBottomLeftRadius: token('radius.large'),
			borderBottomRightRadius: token('radius.large'),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ footerBorderRadius, appearance }: { footerBorderRadius?: CSSToken; appearance?: string }) =>
		!footerBorderRadius && appearance !== 'footer' && { borderRadius: token('radius.small') },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullContentOld = styled.form<
	Pick<InlineCreateFormProps, 'appearance' | 'isTimeline'> & {
		paddingLeft: number;
		isMakeSpaceForRovoEnabled: boolean;
	}
>(
	{
		display: 'flex',
		gap: token('space.100'),

		backgroundColor: token('elevation.surface'),
		transition: 'box-shadow 200ms ease-in-out',
		boxShadow: `inset 0 0 0 ${token('border.width.focused')} ${token('color.border.focused')}`,
		boxSizing: 'border-box',
		outline: 'none',
		minHeight: '40px',
		flexDirection: 'row',
		alignItems: 'center',
		height: '40px',
		margin: '0px', // margin: '0px' is needed to override the default margin of the form element
		paddingTop: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		paddingRight: ({ isMakeSpaceForRovoEnabled }) =>
			isMakeSpaceForRovoEnabled ? token('space.300') : token('space.100'),
		paddingBottom: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		paddingLeft: (props) => `${props.paddingLeft}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isTimeline }: { isTimeline?: boolean }) => isTimeline && { borderRadius: 0 },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ appearance, isTimeline }: { appearance?: string; isTimeline?: boolean }) =>
		!isTimeline &&
		appearance === 'footer' && {
			borderBottomLeftRadius: token('radius.large'),
			borderBottomRightRadius: token('radius.large'),
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isTimeline, appearance }: { isTimeline?: boolean; appearance?: string }) =>
		!isTimeline && appearance !== 'footer' && { borderRadius: token('radius.small') },
);

const FullContent = componentWithFG(
	'refactor_issue_table_for_timline',
	FullContentNew,
	FullContentOld,
);

const TextAreaStyles = xcss({
	flex: '1',
	border: 'none',
	outline: 'none',
	margin: '0',
	padding: 'space.050',
	height: '20px',
	resize: 'none',
	backgroundColor: 'elevation.surface',
	color: 'color.text',
	font: 'font.body',
	scrollbarWidth: 'none',
	whiteSpace: 'pre',
	boxSizing: 'content-box',
	fontFamily: 'inherit',
});

// TODO: Maybe we can rewrite to pressable....
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledCreateButton = styled(Button)({
	flex: 'none',
});
